import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldCheck } from "lucide-react";
import { api, apiError, fmtDate, humanize } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandLoader, Logo } from "@/components/brand/Logo";
import { ErrorState } from "@/components/common";

export default function Invite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const next = `/invite?token=${encodeURIComponent(token)}`;

  useEffect(() => {
    sessionStorage.setItem("cq_next", next);
    api.get("/invitations/preview", { params: { token } }).then((r) => setInv(r.data)).catch((e) => setErr(apiError(e)));
  }, [token, next]);

  const accept = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/invitations/accept", { token });
      sessionStorage.removeItem("cq_next");
      localStorage.setItem("cq_ws", data.workspace_id);
      await qc.invalidateQueries({ queryKey: ["workspaces"] });
      nav("/command-centre", { replace: true });
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  if (user === null || (!inv && !err)) return <BrandLoader label="Opening invitation" />;
  const wrongEmail = user && inv && user.email.toLowerCase() !== inv.email;
  return (
    <div className="min-h-screen bg-[#050816] flex items-center justify-center p-6" data-testid="invite-page">
      <div className="w-full max-w-md">
        <div className="mb-8"><Logo size={40} /></div>
        <div className="cq-card p-7">
          {!inv ? <ErrorState message={err} testId="invite-error" /> : (<>
            <div className="cq-label mb-3">Workspace invitation</div>
            <h1 className="font-display text-2xl text-[#F7FAFF]" data-testid="invite-workspace-name">Join {inv.workspace_name}</h1>
            <p className="mt-2 text-sm text-[#AAB6CC]">{inv.invited_by_name} invited <span className="text-[#F7FAFF]">{inv.email}</span> as <span className="text-[#22E6F2]" data-testid="invite-role">{humanize(inv.role)}</span>. Expires {fmtDate(inv.expires_at, { dateStyle: "medium" })}.</p>
            {inv.status !== "pending" ? <div className="mt-5"><ErrorState message={`This invitation is ${inv.status}. Ask the workspace owner for a new one.`} testId="invite-status-error" /></div>
              : !user ? (
                <div className="mt-6 space-y-2">
                  <Link data-testid="invite-signin-link" to={`/login?next=${encodeURIComponent(next)}`} className="cq-btn cq-btn-primary w-full !py-2.5">Sign in as {inv.email}</Link>
                  <Link data-testid="invite-register-link" to={`/register?next=${encodeURIComponent(next)}&email=${encodeURIComponent(inv.email)}`} className="cq-btn cq-btn-ghost w-full !py-2.5">Create an account</Link>
                </div>
              ) : wrongEmail ? (
                <div className="mt-5 space-y-3">
                  <ErrorState message={`You are signed in as ${user.email}. This invitation only works for ${inv.email}.`} testId="invite-wrong-email" />
                  <button data-testid="invite-switch-account" onClick={logout} className="cq-btn cq-btn-ghost w-full">Sign out and switch account</button>
                </div>
              ) : (
                <button data-testid="invite-accept-button" disabled={busy} onClick={accept} className="cq-btn cq-btn-primary w-full !py-2.5 mt-6">{busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Accept and join</button>
              )}
            {err && inv && <div className="mt-4"><ErrorState message={err} testId="invite-accept-error" /></div>}
          </>)}
        </div>
      </div>
    </div>
  );
}
